import { ApiError } from '../../api/client';
import { apiErrorMessage } from '../../api/errorMessage.ts';
import type { Message, TFunction } from '../../i18n/translate.ts';

/**
 * MVP-231: every rule the profile screen enforces, kept out of `ProfileDialog` so it is testable
 * without a DOM — the same split as `features/admin/invites.ts` and `features/admin/audit.ts`.
 *
 * MVP-272 Phase 7: the two form rules hand back a `Message` (their numbers are interpolated, so
 * the sentence can never disagree with the constant it quotes), while `passwordErrorMessage` takes
 * a `t` — its last branch keeps the SERVER's wording, which no dictionary key can express. Both
 * shapes and the reason for each are documented in `i18n/translate.ts`.
 */

/** What the server stores (`parseSelfPatch` slices to 80). Enforced here too so a person is told
 * before the round trip rather than watching their name get quietly cut. */
export const DISPLAY_NAME_MAX = 80;

/** The same floor the invite-redeem form uses (`redeemFormError`) — this product picks a password in
 * exactly two places and they must not disagree about what is long enough. */
export const PASSWORD_MIN = 10;

/** What `PATCH /api/account/me` should be sent, or a problem to show instead. `null` is meaningful:
 * it is how the API spells "no display name", which is what an emptied field means. */
export function parseDisplayName(value: string): { ok: true; value: string | null } | { ok: false; error: Message } {
  const trimmed = value.trim();
  if (trimmed.length > DISPLAY_NAME_MAX) {
    return { ok: false, error: { key: 'account.displayNameTooLong', params: { max: DISPLAY_NAME_MAX } } };
  }
  return { ok: true, value: trimmed || null };
}

/**
 * The first problem with the change-password form, or null when it may be submitted.
 *
 * The "must not match the current one" rule is client-side only — the server would happily accept
 * it — because a password change that changes nothing is the one outcome nobody wants and the
 * person cannot see they got.
 */
export function passwordFormError(fields: { current: string; next: string; confirm: string }): Message | null {
  if (!fields.current) return { key: 'account.needCurrentPassword' };
  if (fields.next.length < PASSWORD_MIN) {
    return { key: 'account.newPasswordTooShort', params: { min: PASSWORD_MIN } };
  }
  if (fields.next !== fields.confirm) return { key: 'account.passwordMismatch' };
  if (fields.next === fields.current) return { key: 'account.passwordUnchanged' };
  return null;
}

/**
 * Strapi's change-password failures in a sentence a person can act on.
 *
 * The wrong-current-password case is a plain 400 carrying the plugin's own English, so it is
 * matched on the message rather than a code — there is no code to match. Everything else keeps the
 * server's wording instead of inventing one, because an unrecognised 400 here means the server
 * knows something this build does not.
 */
export function passwordErrorMessage(err: unknown, t: TFunction): string {
  if (!(err instanceof ApiError)) {
    return t('common.serverUnreachableRetry');
  }
  if (err.status === 400 && /current password/i.test(err.message)) {
    return t('account.wrongCurrentPassword');
  }
  if (err.status === 429) {
    return t('account.tooManyAttempts');
  }
  // MVP-371: an unrecognised 400 here used to return the server's own wording, which for this
  // endpoint is the users-permissions plugin's English default on a Thai-default screen. The
  // account holder is signed in and one click from trying again; a translated "that didn't work"
  // serves them better than a sentence they cannot read.
  return apiErrorMessage(err, t('account.passwordChangeFailed'), t);
}
